/**
 * Main Layout Component
 * 3-column layout: Sidebar + Message Area + Right Panel
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Sidebar from './Sidebar';
import MessageArea from './MessageArea';
import { RightPanel } from './RightPanel';
import { UnifiedHeader } from './UnifiedHeader';
import { InlineSearch } from '@/components/search/InlineSearch';
import { SettingsModal } from '@/components/settings/SettingsModal';
import { WhatsNewModal, ChangelogPage, shouldShowWhatsNew, CURRENT_VERSION } from '@/components/changelog';
import { VoiceCallWindow, VoiceChannelWindow } from '@/components/voice';
import { IncomingCallDialog } from '@/components/voice/IncomingCallDialog';
import { useVoice } from '@/contexts/VoiceContext';
import { VoiceStatusBar } from '@/components/voice/VoiceStatusBar';
import { UpdateNotification } from '@/components/common/UpdateNotification';
import { ChatConnectionBanner } from '@/components/common/ChatConnectionBanner';
import { AdminPanel } from '@/components/admin/AdminPanel';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useChannels } from '@/hooks/useChannels';
import { useAppStore } from '@/store/app.store';
import socketClient from '@/services/socket.client';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import type { KeyboardShortcut } from '@/hooks/useKeyboardShortcuts';
import { useKeyboardShortcutsContext } from '@/contexts/KeyboardShortcutsContext';
import { KeyboardShortcutsModal } from '@/components/keyboard-shortcuts/KeyboardShortcutsModal';
import { DownloadBanner } from '@/components/download/DownloadBanner';
import QuickSwitcher from '@/components/common/QuickSwitcher';
import { useElectronDragGuard } from '@/hooks/useElectronDragGuard';

const MainLayout = () => {
  const { sidebarCollapsed, activeChannelId, activeDMThreadId, searchOpen, setSearchOpen, setActiveChannel } = useAppStore();
  const { data: channels } = useChannels();
  const { voiceState } = useVoice();
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [whatsNewModalOpen, setWhatsNewModalOpen] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [adminPanelInitialSection, setAdminPanelInitialSection] = useState<'users' | 'smtp' | 'storage' | 'analytics'>('users');
  const [quickSwitcherOpen, setQuickSwitcherOpen] = useState(false);

  // Global Cmd/Ctrl-K quick switcher. This deliberate combo opens the palette
  // even when focus is inside an input (it never hijacks normal typing).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setQuickSwitcherOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close changelog on navigation (adjust state during render on change)
  const navKey = `${activeChannelId}|${activeDMThreadId}`;
  const [prevNavKey, setPrevNavKey] = useState(navKey);
  if (navKey !== prevNavKey) {
    setPrevNavKey(navKey);
    setShowChangelog(false);
  }

  // Connect to WebSocket on mount
  useWebSocket();

  // Keep the browser tab title in sync with active conversation + unread count
  useDocumentTitle();

  // Prevent drag-region conflicts with file drag-and-drop in Electron
  const fileDragActive = useElectronDragGuard();

  // Auto-select default channel when none is selected
  useEffect(() => {
    if (!activeChannelId && !activeDMThreadId && channels?.length) {
      const defaultChannel = channels.find(ch => ch.is_default && !ch.is_archived);
      if (defaultChannel) {
        setActiveChannel(defaultChannel.id);
      } else {
        const firstChannel = channels.find(ch => !ch.is_archived);
        if (firstChannel) {
          setActiveChannel(firstChannel.id);
        }
      }
    }
  }, [channels, activeChannelId, activeDMThreadId, setActiveChannel]);

  // Show "What's New" modal for new versions
  useEffect(() => {
    if (shouldShowWhatsNew()) {
      const timer = setTimeout(() => {
        setWhatsNewModalOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for version updates from server
  useEffect(() => {
    const isNewerVersion = (server: string, client: string): boolean => {
      const sParts = server.split('.').map(Number);
      const cParts = client.split('.').map(Number);
      for (let i = 0; i < Math.max(sParts.length, cParts.length); i++) {
        const s = sParts[i] || 0;
        const c = cParts[i] || 0;
        if (s > c) return true;
        if (s < c) return false;
      }
      return false;
    };

    const handleVersionCheck = (data: { version: string }) => {
      if (isNewerVersion(data.version, CURRENT_VERSION)) {
        console.log(`🔄 New version available: ${data.version} (current: ${CURRENT_VERSION})`);
        toast.info('Налична е нова версия!', {
          description: 'Кликнете тук за да обновите',
          duration: Infinity,
          action: {
            label: 'Обнови',
            onClick: () => window.location.reload(),
          },
        });
      }
    };

    socketClient.onVersionCurrent(handleVersionCheck);
    socketClient.onVersionUpdate(handleVersionCheck);

    return () => {
      socketClient.offVersionCurrent(handleVersionCheck);
      socketClient.offVersionUpdate(handleVersionCheck);
    };
  }, []);

  // Keyboard shortcuts
  const { closeHelpModal, toggleHelpModal } = useKeyboardShortcutsContext();

  const shortcuts: KeyboardShortcut[] = [
    {
      key: 'f',
      ctrl: true,
      action: () => setSearchOpen(!searchOpen),
      description: 'Отвори търсачката',
      category: 'navigation',
    },
    {
      key: '?',
      ctrl: true,
      action: () => toggleHelpModal(),
      description: 'Покажи клавишни комбинации',
      category: 'general',
    },
    {
      key: 'Escape',
      action: () => {
        if (searchOpen) setSearchOpen(false);
        else if (settingsModalOpen) setSettingsModalOpen(false);
        else if (adminPanelOpen) setAdminPanelOpen(false);
        else closeHelpModal();
      },
      description: 'Затвори отворени прозорци',
      category: 'general',
      preventDefault: false,
    },
    {
      key: ',',
      ctrl: true,
      action: () => setSettingsModalOpen(true),
      description: 'Отвори настройки',
      category: 'general',
    },
  ];

  useKeyboardShortcuts({ enabled: true, shortcuts });

  const openSettingsModal = () => setSettingsModalOpen(true);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-dark-bg-primary">
      {/* Auto-update notification (Electron/Capacitor only) */}
      <UpdateNotification />

      {/* Chat socket reconnect banner */}
      <ChatConnectionBanner />

      {/* Sidebar - Left Column */}
      <nav
        aria-label="Странична навигация"
        className={`transition-all duration-300 ${
          sidebarCollapsed ? 'w-0' : 'w-64'
        } flex-shrink-0 overflow-hidden`}
      >
        <Sidebar onOpenSettings={openSettingsModal} fileDragActive={fileDragActive} />
      </nav>

      {/* Message Area - Center Column */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Unified Header */}
        <header className="contents">
          <UnifiedHeader
            onOpenSettings={openSettingsModal}
            fileDragActive={fileDragActive}
            onOpenAdminPanel={(section) => {
              setAdminPanelInitialSection(section);
              setAdminPanelOpen(true);
            }}
            onOpenWhatsNew={() => setWhatsNewModalOpen(true)}
            onShowChangelog={() => setShowChangelog(true)}
          />
        </header>

        {/* Inline Search (slides down below header) */}
        {searchOpen && (
          <InlineSearch onClose={() => setSearchOpen(false)} />
        )}

        {/* Message Area, Changelog, or other views */}
        <main className="flex-1 overflow-hidden">
          {showChangelog ? (
            <ChangelogPage onBack={() => setShowChangelog(false)} />
          ) : (
            <MessageArea />
          )}
        </main>
      </div>

      {/* Right Panel - Always on when conversation is active */}
      {(activeChannelId || activeDMThreadId) && (
        <aside aria-label="Панел с детайли" className="contents">
          <RightPanel />
        </aside>
      )}

      {/* Settings Modal */}
      <SettingsModal
        isOpen={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
      />

      {/* Admin Panel Overlay */}
      <AdminPanel
        isOpen={adminPanelOpen}
        onClose={() => setAdminPanelOpen(false)}
        initialSection={adminPanelInitialSection}
        onShowChangelog={() => {
          setAdminPanelOpen(false);
          setShowChangelog(true);
        }}
      />

      {/* Incoming Call Dialog */}
      {voiceState.incomingCall && (
        <IncomingCallDialog incomingCall={voiceState.incomingCall} />
      )}

      {/* Voice Call Window (DM calls) */}
      <VoiceCallWindow />

      {/* Voice Channel Window (Channel voice) */}
      <VoiceChannelWindow />

      {/* Voice Status Bar (Persistent bottom bar when in voice) */}
      <VoiceStatusBar />

      {/* Quick Switcher (Cmd/Ctrl-K) */}
      {quickSwitcherOpen && (
        <QuickSwitcher onClose={() => setQuickSwitcherOpen(false)} />
      )}

      {/* Keyboard Shortcuts Help Modal */}
      <KeyboardShortcutsModal />

      {/* Download App Banner - only in browser */}
      <DownloadBanner />

      {/* What's New Modal */}
      <WhatsNewModal
        isOpen={whatsNewModalOpen}
        onClose={() => setWhatsNewModalOpen(false)}
        onViewFullHistory={() => setShowChangelog(true)}
      />
    </div>
  );
};

export default MainLayout;
